import { useMemo, useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import Pagination from "@/components/base/Pagination";
import SoftwareCard from "@/components/base/SoftwareCard";
import { getCategoryById, getSoftwareByCategory, parseSizeToMB } from "@/utils/software";
import FilterPanel, { type FilterState } from "./components/FilterPanel";

type SortKey = "updated" | "downloads" | "rating";

const sortOptions: { value: SortKey; label: string }[] = [
  { value: "updated", label: "按更新时间" },
  { value: "downloads", label: "按下载量" },
  { value: "rating", label: "按评分" },
];

export default function CategoryPage() {
  const { categoryId = "" } = useParams();
  const category = getCategoryById(categoryId);

  const [activeSub, setActiveSub] = useState("all");
  const [filters, setFilters] = useState<FilterState>({ platforms: [], price: "all", size: "all" });
  const [sort, setSort] = useState<SortKey>("updated");
  const [pageSize, setPageSize] = useState(8);
  const [page, setPage] = useState(1);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  useEffect(() => {
    setActiveSub("all");
    setFilters({ platforms: [], price: "all", size: "all" });
    setSort("updated");
    setPage(1);
  }, [categoryId]);

  const baseList = useMemo(() => (category ? getSoftwareByCategory(category.id) : []), [category]);

  const filtered = useMemo(() => {
    let list = [...baseList];
    if (activeSub !== "all") {
      list = list.filter((s) => s.tags.includes(activeSub));
    }
    if (filters.platforms.length > 0) {
      list = list.filter((s) => s.platforms.some((p) => filters.platforms.includes(p)));
    }
    if (filters.price !== "all") {
      list = list.filter((s) => s.price === filters.price);
    }
    if (filters.size !== "all") {
      list = list.filter((s) => {
        const mb = parseSizeToMB(s.size);
        if (filters.size === "small") return mb < 100;
        if (filters.size === "medium") return mb >= 100 && mb <= 500;
        return mb > 500;
      });
    }
    if (sort === "downloads") list.sort((a, b) => b.downloads - a.downloads);
    else if (sort === "rating") list.sort((a, b) => b.rating - a.rating);
    else list.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
    return list;
  }, [baseList, activeSub, filters, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  const togglePlatform = (p: string) => {
    setFilters((f) => ({
      ...f,
      platforms: f.platforms.includes(p) ? f.platforms.filter((x) => x !== p) : [...f.platforms, p],
    }));
    setPage(1);
  };

  const resetAll = () => {
    setActiveSub("all");
    setFilters({ platforms: [], price: "all", size: "all" });
    setSort("updated");
    setPage(1);
  };

  if (!category) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <Navbar />
        <main className="flex-1 w-full max-w-[1240px] mx-auto px-4 md:px-6 pt-28 pb-20">
          <div className="bg-background-100 rounded-lg border border-background-200 p-10 text-center">
            <span className="flex items-center justify-center w-14 h-14 mx-auto rounded-full bg-background-200 text-secondary-500 text-3xl">
              <i className="ri-emotion-sad-line" aria-hidden="true"></i>
            </span>
            <h1 className="mt-4 font-heading text-lg font-bold text-foreground-950">未找到该软件分类</h1>
            <p className="mt-2 text-sm text-foreground-600">该分类可能已调整或暂未开放，请返回首页浏览其他分类。</p>
            <Link to="/" className="mt-5 inline-flex items-center gap-2 h-10 px-5 text-sm rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Navbar />
      <main className="flex-1">
        <div className="bg-background-100 border-b border-background-200">
          <div className="w-full max-w-[1240px] mx-auto px-4 md:px-6 pt-24 pb-8">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "软件分类", to: "/" }, { label: category.name }]} />
            <div className="mt-4 flex items-start gap-4">
              <span className="flex items-center justify-center w-14 h-14 rounded-lg bg-primary-500 text-background-50 text-3xl shrink-0">
                <i className={category.icon} aria-hidden="true"></i>
              </span>
              <div>
                <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">{category.name}手机软件</h1>
                <p className="mt-1.5 text-sm text-foreground-600 leading-relaxed max-w-3xl">{category.description}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full max-w-[1240px] mx-auto px-4 md:px-6 py-8">
          <div className="flex flex-col lg:flex-row gap-6">
            <aside className="hidden lg:block w-[240px] shrink-0">
              <div className="sticky top-20 bg-background-50 rounded-lg border border-background-200 p-5">
                <FilterPanel
                  subcategories={category.subcategories}
                  activeSub={activeSub}
                  onSubChange={(v) => {
                    setActiveSub(v);
                    setPage(1);
                  }}
                  filters={filters}
                  onTogglePlatform={togglePlatform}
                  onPriceChange={(v) => {
                    setFilters((f) => ({ ...f, price: v }));
                    setPage(1);
                  }}
                  onSizeChange={(v) => {
                    setFilters((f) => ({ ...f, size: v }));
                    setPage(1);
                  }}
                  onReset={resetAll}
                />
              </div>
            </aside>

            <div className="flex-1 min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setMobileFilterOpen((v) => !v)}
                    className="lg:hidden inline-flex items-center gap-1.5 h-9 px-3 text-sm rounded-md border border-background-200 text-foreground-700 cursor-pointer whitespace-nowrap"
                  >
                    <span className="flex items-center justify-center w-4 h-4"><i className="ri-filter-3-line" aria-hidden="true"></i></span>
                    筛选
                  </button>
                  <p className="text-sm text-foreground-600">
                    共 <span className="font-heading font-semibold text-primary-600">{filtered.length}</span> 款软件
                  </p>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  <select
                    value={sort}
                    onChange={(e) => {
                      setSort(e.target.value as SortKey);
                      setPage(1);
                    }}
                    className="h-9 px-3 text-sm rounded-md border border-background-200 bg-background-50 text-foreground-700 focus:outline-none focus:border-primary-400 cursor-pointer"
                  >
                    {sortOptions.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(1);
                    }}
                    className="h-9 px-3 text-sm rounded-md border border-background-200 bg-background-50 text-foreground-700 focus:outline-none focus:border-primary-400 cursor-pointer"
                  >
                    <option value={8}>每页 8 条</option>
                    <option value={12}>每页 12 条</option>
                    <option value={20}>每页 20 条</option>
                  </select>
                </div>
              </div>

              {mobileFilterOpen && (
                <div className="lg:hidden mb-4 bg-background-50 rounded-lg border border-background-200 p-5">
                  <FilterPanel
                    subcategories={category.subcategories}
                    activeSub={activeSub}
                    onSubChange={(v) => {
                      setActiveSub(v);
                      setPage(1);
                    }}
                    filters={filters}
                    onTogglePlatform={togglePlatform}
                    onPriceChange={(v) => {
                      setFilters((f) => ({ ...f, price: v }));
                      setPage(1);
                    }}
                    onSizeChange={(v) => {
                      setFilters((f) => ({ ...f, size: v }));
                      setPage(1);
                    }}
                    onReset={resetAll}
                  />
                </div>
              )}

              {paged.length > 0 ? (
                <>
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                    {paged.map((software, i) => (
                      <SoftwareCard key={software.id} software={software} index={i} />
                    ))}
                  </div>
                  <Pagination current={safePage} total={totalPages} onChange={setPage} />
                </>
              ) : (
                <div className="bg-background-100 rounded-lg border border-background-200 p-10 text-center">
                  <span className="flex items-center justify-center w-14 h-14 mx-auto rounded-full bg-background-200 text-secondary-500 text-3xl">
                    <i className="ri-search-eye-line" aria-hidden="true"></i>
                  </span>
                  <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">没有符合条件的软件</h3>
                  <p className="mt-2 text-sm text-foreground-600">试试调整或重置筛选条件，查看更多 {category.name} 应用。</p>
                  <button
                    type="button"
                    onClick={resetAll}
                    className="mt-5 inline-flex items-center gap-2 h-10 px-5 text-sm rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <span className="flex items-center justify-center w-4 h-4"><i className="ri-refresh-line" aria-hidden="true"></i></span>
                    重置筛选条件
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}